import { ReactNode, useCallback, useMemo } from 'react';
import ProjectNewsContext from '../context/ProjectNewsContext';
import { useSelector } from 'react-redux';
import { RootReducerState } from '../../../apps/main/rootReducer';
import {
  ProjectNews,
  ProjectNewsCategoryKey,
  ProjectNewsDto,
} from '../../../models/Project';
import { ProjectNewsMeta } from '../reducers/projectNews';

interface ProjectNewsContextProviderProps {
  children: ReactNode;
}

const ProjectNewsContextProvider = (props: ProjectNewsContextProviderProps) => {
  //#region ------------------------------ Defaults
  const { children } = props;
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const projectNewsReduxState = useSelector(
    (state: RootReducerState) => state.projects.projectNews
  );

  const projectNewsById: ProjectNews = useMemo(() => {
    return projectNewsReduxState.byId ?? {};
  }, [projectNewsReduxState]);

  const projectNewsMeta: ProjectNewsMeta = useMemo(() => {
    return (
      projectNewsReduxState.meta ?? {
        isFetching: false,
        hasError: false,
      }
    );
  }, [projectNewsReduxState]);

  const projectNewsCountsById = useSelector(
    (state: RootReducerState) => state.projects.projectNewsCounts.byId
  );

  const projectNewsCountsMeta = useSelector(
    (state: RootReducerState) => state.projects.projectNewsCounts.meta
  );
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const getProjectNewsByProjectId = useCallback(
    (projectId: string): ProjectNewsCategoryKey | undefined => {
      return (
        projectNewsById[projectId?.toUpperCase()] ||
        projectNewsById[projectId?.toLowerCase()] ||
        projectNewsById[projectId]
      );
    },
    [projectNewsById]
  );

  const getProjectNewsDto = useCallback(
    (projectId: string, projectNewsCategoryKey: string): ProjectNewsDto[] => {
      const projectNews = getProjectNewsByProjectId(projectId);
      if (!projectNews) {
        return [];
      }
      return projectNews[projectNewsCategoryKey] ?? [];
    },
    [getProjectNewsByProjectId]
  );

  const getProjectNewsCountsByProjectId = useCallback(
    (projectId: string) => {
      return (
        projectNewsCountsById?.[projectId?.toUpperCase()] ||
        projectNewsCountsById?.[projectId?.toLowerCase()] ||
        projectNewsCountsById?.[projectId]
      );
    },
    [projectNewsCountsById]
  );

  const getProjectIdsWithNews = useCallback(() => {
    return Object.entries(projectNewsById).reduce(
      (array, [projectId, countsByCategory]) => {
        if (Object.keys(countsByCategory).length > 0) {
          const counts = Object.values(countsByCategory).reduce(
            (sum, count) => sum + count.length,
            0
          );
          if (counts > 0) {
            array.push(projectId);
          }
        }
        return array;
      },
      [] as string[]
    );
  }, [projectNewsById]);
  //#endregion

  //#region ------------------------------ Effects
  //#endregion

  return (
    <ProjectNewsContext.Provider
      value={{
        projectNewsMeta,
        projectNewsCountsMeta,
        getProjectNewsByProjectId,
        getProjectNewsDto,
        getProjectNewsCountsByProjectId,
        getProjectIdsWithNews,
      }}
    >
      {children}
    </ProjectNewsContext.Provider>
  );
};

export default ProjectNewsContextProvider;
